import { usePortalMessage, type PortalMessage, resolvePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `Organization` → My security — `/me/security` (plan-014 C1; design §27.15.1; FR12-SEC-01/03). Drawn to the
 * prototype's `me` page (`design/WeTalk.dc.html`, the Organization block renderer): kicker, title, lede, the tab
 * strip, then three blocks — "Ways to sign in" (a list; "You cannot remove the last one."), "Second step" (the
 * authenticator app and the recovery codes, "n of 10 left") and "Where you are signed in" (a table with "Sign out
 * everywhere else").
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04). The prototype's toasts are the real outcomes, shown where the prototype
 * shows them (under the tabs). Where the API cannot do something yet the control is not drawn: "Link" appears only
 * when a provider is `linkable` — AMENDED 2026-10-25 (plan-028 §39.8): its sign-in is switched on and it is not linked
 * yet. "Link" confirms the person first (as every change here), then goes to the provider, which brings them back here.
 *
 * FRESH CONFIRMATION. "Changes here ask for your password again" (the lede). A change answered `step_up_required`
 * opens the confirmation — the password (`stepUpAuthSession`) or, for a person with no password, the authenticator
 * (`answerMfaChallenge`) — and is retried once.
 *
 * AMENDED 2026-10-08 (INT, security review CR-1's console half): a person who holds an authenticator confirms with it,
 * never the password (the API refuses a password step-up then, and every change here answers `401 assurance_required`
 * until this sign-in has passed the authenticator). Such an answer opens the same confirmation for the authenticator
 * code — or one recovery code — and the change is retried once. When the last recovery code is used the Second step
 * block says so and offers new ones (security review M-6: the authenticator stays required; the codes are the way
 * back in without the phone).
 *
 * AMENDED 2026-10-25 (ADR 0080 amendment, design §39.15): a person with NO password and NO authenticator — who signs in
 * only with Google or Microsoft — confirms with that provider: the confirmation offers "Continue with Google/Microsoft
 * to confirm it's you" for each provider they have linked, the browser goes there and comes back here with
 * `?step_up=confirmed | cancelled | failed`, which the page shows once and then drops from the address. The change that
 * asked is made again by the person (a round trip cannot retry it); the confirmation lasts a few minutes.
 *
 * AMENDED 2026-11-03 (owner decision "Both cases", design §16.37 amendment): a person with NO password, NO authenticator
 * and NO Google or Microsoft — someone who joined from an invitation with an emailed code — confirms with an emailed
 * code: "Email me a code" (`requestSignInCode` to their own address), then the code (`stepUpWithEmailCode`), and the
 * change is retried once. So "Set a password" and "Add an authenticator app" work for them too.
 *
 * SECRETS SHOWN ONCE. The QR code is drawn in the browser from the otpauth URI and never sent anywhere; recovery
 * codes are shown in a dialog that only its own button closes, and are not kept in the query cache.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { HELP_PAGE_READING_REVISION } from "@wetalk/domain";
import { Button, Dialog, Field, Icon, Input, type PhosphorIcon } from "@wetalk/ui";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import type { ConsoleGateway } from "../../api/gateway.js";
import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import type { PersonDeviceView, RemovableSignInMethod, SignInMethodsView } from "../../api/slice/personal-security.js";
import { GLYPH } from "../../shell/glyph.js";

import {
  METHOD_WORD,
  agoText,
  deviceText,
  passwordChangedText,
  placeText,
  recoveryLeftText,
  shortDateText,
  signedOutText,
  unlinkedText,
} from "./personal-security-copy.js";
import { QrCode } from "./qr-code.js";

import "./personal-security.css";

/** How a Google or Microsoft confirmation came back (ADR 0080 amendment 2026-10-25): the API's `?step_up=` words. */
export const STEP_UP_RETURN = ["confirmed", "cancelled", "failed"] as const;
export type StepUpReturn = (typeof STEP_UP_RETURN)[number];

/** The page's search parameters: the page has one tab; `step_up` only on the way back from a provider confirmation. */
export interface PersonalSecuritySearch {
  readonly step_up?: StepUpReturn;
}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens
 * a valid page.
 */
export function personalSecuritySearchOf(raw: Readonly<Record<string, unknown>>): PersonalSecuritySearch {
  const stepUp = raw["step_up"];
  return (STEP_UP_RETURN as readonly unknown[]).includes(stepUp) ? { step_up: stepUp as StepUpReturn } : {};
}

/** The notice a provider confirmation's return shows, once. */
export function stepUpReturnToast(word: StepUpReturn | undefined): { readonly text: PortalMessage; readonly tone: "done" | "problem" } | null {
  switch (word) {
    case undefined:
      return null;
    case "confirmed":
      return { text: () => portalText("core", "personal_security.step_up_confirmed"), tone: "done" };
    case "cancelled":
      return { text: () => portalText("core", "personal_security.step_up_cancelled"), tone: "problem" };
    case "failed":
      return { text: () => portalText("core", "personal_security.step_up_failed"), tone: "problem" };
  }
}

export interface PersonalSecurityScreenProps {
  readonly search: PersonalSecuritySearch;
  readonly onSearch: (next: PersonalSecuritySearch) => void;
}

/** This page's query keys (the person's own, the same in every organization). */
export const PERSONAL_SECURITY_KEY = {
  profile: ["personal-security", "profile"] as const,
  // ADR 0072 (2026-10-18): whether the help assistant may read the whole page.
  helpReading: ["personal-security", "help-page-reading"] as const,
  methods: ["personal-security", "sign-in-methods"] as const,
  devices: ["personal-security", "devices"] as const,
};

/**
 * The prototype's glyphs: `ph-password`, `ph-google-logo`, `ph-microsoft-outlook-logo`, `ph-device-mobile` and `ph-key`,
 * all from the console's one glyph registry (`shell/glyph.ts`; the four were registered by plan-014 SC-P).
 */
const ITEM_GLYPH: Readonly<Record<"password" | "google" | "microsoft" | "authenticator" | "recovery" | "helpReading", PhosphorIcon>> = {
  helpReading: GLYPH.eye,
  password: GLYPH.password,
  google: GLYPH.googleLogo,
  microsoft: GLYPH.microsoftOutlookLogo,
  authenticator: GLYPH.deviceMobile,
  recovery: GLYPH.key,
};

type Tone = "accent" | "neutral";

interface ItemAction {
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
}

/** One row of a prototype `list` block. */
function Item(props: {
  readonly glyph: PhosphorIcon;
  readonly title: string;
  readonly tag: string | null;
  readonly tone: Tone;
  readonly dek: string;
  readonly meta?: string;
  readonly action?: ItemAction | null;
}): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="ps-item">
      <span className={`ps-item-icon ps-tone-${props.tone}`}>
        <Icon glyph={props.glyph} size="lg" />
      </span>
      <span className="ps-item-body">
        <span className="ps-item-head">
          <span className="ps-item-title">{props.title}</span>
          {props.tag === null ? null : <span className={`tag tag-${props.tone} ps-item-tag`}>{props.tag}</span>}
        </span>
        <span className="ps-item-dek">{props.dek}</span>
      </span>
      <span className="ps-item-side">
        {props.meta === undefined ? null : <span className="ps-item-meta">{props.meta}</span>}
        {props.action === undefined || props.action === null ? null : (
          <Button variant="secondary" onClick={props.action.onClick} disabled={props.action.disabled === true}>
            {props.action.label}
          </Button>
        )}
      </span>
    </div>
  );
}

/** A block's head: title, dek and the buttons on the right. */
function BlockHead(props: { readonly title: string; readonly dek?: string; readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("core");
  return (
    <div className="ps-block-head">
      <div className="ps-block-heading">
        <p className="ps-block-title">{props.title}</p>
        {props.dek === undefined ? null : <p className="ps-block-dek">{props.dek}</p>}
      </div>
      {props.children === undefined ? null : <div className="ps-block-buttons">{props.children}</div>}
    </div>
  );
}

// ── fresh confirmation ────────────────────────────────────────────────────────────────────────────────────────

interface PendingConfirmation {
  readonly retry: () => Promise<void>;
  /** `true` when the API asked for the authenticator itself (`401 assurance_required`, security review CR-1). */
  readonly authenticator: boolean;
}

function isStepUpRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === "step_up_required";
}

/** Security review CR-1: a change refused until this sign-in has passed the person's authenticator. */
function isAssuranceRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === "assurance_required";
}

function ConfirmDialog(props: {
  readonly gateway: ConsoleGateway;
  readonly methods: SignInMethodsView;
  readonly pending: PendingConfirmation | null;
  readonly onDone: () => void;
  readonly onProblem: (text: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const [secret, setSecret] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [withRecoveryCode, setWithRecoveryCode] = useState(false);
  // CR-1: whoever holds an authenticator confirms with it (a password step-up is refused then), as does a person
  // with no password, and any change the API answered `assurance_required`.
  const withPassword = props.methods.password.enabled && props.methods.totp.state !== "active" && props.pending?.authenticator !== true;
  // ADR 0080 amendment 2026-10-25: neither a password nor an authenticator — the provider they sign in with confirms.
  const withProvider = !props.methods.password.enabled && props.methods.totp.state !== "active" && props.pending?.authenticator !== true;
  const linkedProvider = (["google", "microsoft"] as const).filter((provider) => props.methods[provider].linked);
  // 2026-11-03: no password, no authenticator and no provider either — the mailbox is their one way in, so a code
  // emailed to it confirms them.
  const withEmailCode = withProvider && linkedProvider.length === 0;
  const profile = useQuery({
    queryKey: PERSONAL_SECURITY_KEY.profile,
    queryFn: () => props.gateway.readMyProfile(),
    enabled: withEmailCode && props.pending !== null,
  });
  const [codeSent, setCodeSent] = useState(false);
  const askForCode = useMutation({
    mutationFn: async () => {
      const email = profile.data?.email;
      if (email === undefined) throw new Error("the profile is not read yet");
      return props.gateway.requestSignInCode(email);
    },
    onSuccess: () => {
      setSecret("");
      setProblem(null);
      setCodeSent(true);
    },
    onError: (raised: Error) => {
      setProblem(() => (apiProblemText(raised, portalText("core", "account_admin.that_change_could_not_be_made_0e975e"))));
    },
  });
  const factor = withRecoveryCode ? "recovery_code" : "totp";
  const confirmWithProvider = useMutation({
    mutationFn: async (provider: "google" | "microsoft") => {
      const started = await props.gateway.beginProviderStepUp(provider, "/me/security");
      window.location.assign(started.startUrl);
    },
    onError: (raised: Error, provider) => {
      props.onDone();
      props.onProblem(() => (apiProblemText(raised, portalText("core", "personal_security.provider_step_up_could_not_start", { value0: METHOD_WORD[provider] }))));
    },
  });
  const confirm = useMutation({
    mutationFn: async () => {
      if (withPassword) await props.gateway.stepUpAuthSession(secret);
      else if (withEmailCode) await props.gateway.stepUpWithEmailCode(secret.replace(/\s/g, ""));
      else await props.gateway.answerMfaChallenge(factor, secret);
      await props.pending?.retry();
    },
    onSuccess: () => {
      setSecret("");
      setProblem(null);
      setWithRecoveryCode(false);
      setCodeSent(false);
      props.onDone();
    },
    onError: (raised: Error) => {
      if (raised instanceof ApiError && (raised.code === "credential_invalid" || raised.code === "mfa_factor_invalid")) {
        setProblem(
          () => (withPassword
            ? portalText("core", "start.that_password_is_not_right_930b19")
            : withEmailCode
              ? portalText("core", "personal_security.email_code_wrong")
              : withRecoveryCode
              ? portalText("core", "auth.that_recovery_code_is_not_right_or_it_was_used_already_916ef9")
              : portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e")),
        );
        return;
      }
      setSecret("");
      props.onDone();
      props.onProblem(() => (apiProblemText(raised, portalText("core", "account_admin.that_change_could_not_be_made_0e975e"))));
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (secret === "") return;
    confirm.mutate();
  };
  return (
    <Dialog
      open={props.pending !== null}
      title={portalText("core", "account_admin.confirm_it_is_you_e6fe51")}
      onClose={() => {
        setSecret("");
        setProblem(null);
        setWithRecoveryCode(false);
        setCodeSent(false);
        props.onDone();
      }}
    >
      {withEmailCode ? (
        <form className="ps-dialog-form" onSubmit={submit}>
          <p className="ps-dialog-text">
            {codeSent
              ? portalText("core", "personal_security.email_code_sent", { value0: profile.data?.email ?? "" })
              : portalText("core", "personal_security.email_code_lede", { value0: profile.data?.email ?? "" })}
          </p>
          {codeSent ? (
            <Field label={portalText("core", "auth.sign_in_code_6af151")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={secret}
                  onChange={(event) => {
                    setSecret(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
          ) : problem === null ? null : (
            <p className="wt-field-error" role="alert">{problem}</p>
          )}
          <div className="ps-dialog-actions">
            {codeSent ? (
              <>
                <Button variant="secondary" disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
                  {portalText("core", "auth.send_a_new_code_91904a")}
                </Button>
                <Button variant="primary" type="submit" disabled={confirm.isPending || secret === ""}>
                  {confirm.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "account_admin.confirm_04a212")}
                </Button>
              </>
            ) : (
              <Button variant="primary" disabled={askForCode.isPending || profile.data === undefined} onClick={() => askForCode.mutate()}>
                {portalText("core", "personal_security.email_me_a_code")}
              </Button>
            )}
          </div>
        </form>
      ) : withProvider ? (
        <div className="ps-dialog-form">
          <p className="ps-dialog-text">{portalText("core", "personal_security.confirm_with_provider_lede")}</p>
          <div className="ps-dialog-actions">
            {linkedProvider.map((provider) => (
              <Button
                key={provider}
                variant="primary"
                disabled={confirmWithProvider.isPending}
                onClick={() => {
                  confirmWithProvider.mutate(provider);
                }}
              >
                {portalText("core", "personal_security.continue_with_provider_to_confirm", { value0: METHOD_WORD[provider] })}
              </Button>
            ))}
          </div>
        </div>
      ) : (
      <form className="ps-dialog-form" onSubmit={submit}>
        <p className="ps-dialog-text">
          {withPassword
            ? portalText("core", "personal_security.changes_to_your_sign_in_ask_for_your_password_again_ff9dff")
            : withRecoveryCode
              ? portalText("core", "personal_security.enter_one_of_your_recovery_codes_it_works_once_d40034")
              : portalText("core", "personal_security.changes_to_your_sign_in_ask_for_a_code_from_your_authenticator_ap_cb37ff")}
        </p>
        <Field
          label={withPassword ? portalText("core", "auth.password_8be3c9") : withRecoveryCode ? portalText("core", "auth.recovery_code_3cb5fd") : portalText("core", "auth.6_digit_code_0c52ea")}
          {...(problem === null ? {} : { error: problem })}
        >
          {(control) => (
            <Input
              {...control}
              type={withPassword ? "password" : "text"}
              inputMode={withPassword || withRecoveryCode ? undefined : "numeric"}
              autoComplete={withPassword ? "current-password" : "one-time-code"}
              value={secret}
              onChange={(event) => {
                setSecret(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        {withPassword ? null : (
          <button
            type="button"
            className="ps-link"
            onClick={() => {
              setWithRecoveryCode((held) => !held);
              setSecret("");
              setProblem(null);
            }}
          >
            {withRecoveryCode ? portalText("core", "personal_security.use_the_authenticator_app_instead_683c3a") : portalText("core", "auth.use_a_recovery_code_instead_104df4")}
          </button>
        )}
        <div className="ps-dialog-actions">
          <Button variant="primary" type="submit" disabled={confirm.isPending || secret === ""}>
            {confirm.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "account_admin.confirm_04a212")}
          </Button>
        </div>
      </form>
      )}
    </Dialog>
  );
}

// ── the recovery codes, shown once ───────────────────────────────────────────────────────────────────────────

function RecoveryCodesDialog(props: { readonly codes: readonly string[] | null; readonly onClose: () => void }): ReactNode {
  usePortalTranslation("core");
  return (
    <Dialog
      open={props.codes !== null}
      title={portalText("core", "start.your_recovery_codes_781e39")}
      dismissible={false}
      onClose={props.onClose}
      actions={
        <Button variant="primary" onClick={props.onClose}>{portalText("core", "personal_security.i_have_saved_them_1ce73f")}</Button>
      }
    >
      <p className="ps-dialog-text">{portalText("core", "personal_security.each_works_once_instead_of_your_authenticator_app_keep_them_somew_b40242")}</p>
      <ol className="ps-codes" aria-label={portalText("core", "start.recovery_codes_b3f5f8")}>
        {(props.codes ?? []).map((code) => (
          <li key={code} className="ps-code">
            {code.length === 10 ? `${code.slice(0, 5)}-${code.slice(5)}` : code}
          </li>
        ))}
      </ol>
    </Dialog>
  );
}

// ── the authenticator: scan, then confirm ────────────────────────────────────────────────────────────────────

function AuthenticatorDialog(props: {
  readonly gateway: ConsoleGateway;
  readonly otpauthUri: string | null;
  readonly replacing: boolean;
  readonly onClose: () => void;
  readonly onConfirmed: (replaced: boolean, codes: readonly string[] | null) => void;
}): ReactNode {
  usePortalTranslation("core");
  const [code, setCode] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const confirm = useMutation({
    mutationFn: () => props.gateway.confirmTotpEnrolment(code.replace(/\s/g, "")),
    onSuccess: (confirmed) => {
      setCode("");
      setProblem(null);
      props.onConfirmed(confirmed.replaced, confirmed.recoveryCode);
    },
    onError: (raised: Error) => {
      setProblem(
        () => (raised instanceof ApiError && raised.code === "mfa_factor_invalid"
          ? portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e")
          : apiProblemText(raised, portalText("core", "personal_security.the_authenticator_could_not_be_confirmed_726ae8"))),
      );
    },
  });
  const secret = props.otpauthUri === null ? "" : (new URL(props.otpauthUri).searchParams.get("secret") ?? "");
  return (
    <Dialog
      open={props.otpauthUri !== null}
      title={props.replacing ? portalText("core", "personal_security.replace_your_authenticator_app_b5b708") : portalText("core", "personal_security.add_an_authenticator_app_3b631b")}
      onClose={() => {
        setCode("");
        setProblem(null);
        props.onClose();
      }}
    >
      <form
        className="ps-dialog-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim() !== "") confirm.mutate();
        }}
      >
        <p className="ps-dialog-text">{portalText("core", "personal_security.scan_the_new_code_then_enter_a_6_digit_code_to_confirm_f100d1")}</p>
        {props.otpauthUri === null ? null : (
          <div className="ps-scan">
            <QrCode text={props.otpauthUri} label={portalText("core", "start.qr_code_for_your_authenticator_app_1f4d21")} />
            <span className="ps-scan-secret">
              <span className="ps-scan-hint">{portalText("core", "personal_security.cannot_scan_enter_this_key_instead_75b085")}</span>
              <code className="ps-scan-key">{secret.replace(/(.{4})/g, "$1 ").trim()}</code>
            </span>
          </div>
        )}
        <Field label={portalText("core", "auth.6_digit_code_0c52ea")} {...(problem === null ? {} : { error: problem })}>
          {(control) => (
            <Input
              {...control}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => {
                setCode(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <div className="ps-dialog-actions">
          <Button variant="primary" type="submit" disabled={confirm.isPending || code.trim() === ""}>
            {confirm.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "account_admin.confirm_04a212")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── the password ─────────────────────────────────────────────────────────────────────────────────────────────

function PasswordDialog(props: {
  readonly open: boolean;
  readonly wasSet: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (password: string) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const close = (): void => {
    setPassword("");
    setAgain("");
    setProblem(null);
    props.onClose();
  };
  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (password.length < 10) {
      setProblem(() => (portalText("core", "auth.passwords_are_at_least_10_characters_ce1196")));
      return;
    }
    if (password !== again) {
      setProblem(() => (portalText("core", "auth.the_two_passwords_are_not_the_same_83838b")));
      return;
    }
    await props.onSubmit(password);
    setPassword("");
    setAgain("");
    setProblem(null);
  };
  return (
    <Dialog open={props.open} title={props.wasSet ? portalText("core", "personal_security.change_your_password_88dee3") : portalText("core", "personal_security.set_a_password_f5c8f4")} onClose={close}>
      <form
        className="ps-dialog-form"
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <p className="ps-dialog-text">{portalText("core", "personal_security.your_other_devices_are_signed_out_when_it_changes_11b4c6")}</p>
        <Field label={portalText("core", "personal_security.new_password_d850ee")} description={portalText("core", "auth.at_least_10_characters_37e136")} {...(problem === null ? {} : { error: problem })}>
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => {
                setPassword(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <Field label={portalText("core", "personal_security.the_same_password_again_e2fefc")}>
          {(control) => (
            <Input
              {...control}
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(event) => {
                setAgain(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <div className="ps-dialog-actions">
          <Button variant="primary" type="submit">
            {props.wasSet ? portalText("core", "personal_security.change_password_8c6842") : portalText("core", "personal_security.set_password_94408e")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────────────────

export function PersonalSecurityScreen(props: PersonalSecurityScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const methods = useQuery({ queryKey: PERSONAL_SECURITY_KEY.methods, queryFn: () => gateway.readMySignInMethods() });
  const devices = useQuery({ queryKey: PERSONAL_SECURITY_KEY.devices, queryFn: () => gateway.readMyDevices() });
  const helpReading = useQuery({ queryKey: PERSONAL_SECURITY_KEY.helpReading, queryFn: () => gateway.readHelpPageReading() });

  // A provider confirmation's return (`?step_up=`) is shown once, then dropped from the address.
  const [toast, setToast] = useState<{ readonly text: PortalMessage; readonly tone: "done" | "problem" } | null>(() => stepUpReturnToast(props.search.step_up));
  const { onSearch } = props;
  const returned = props.search.step_up;
  useEffect(() => {
    if (returned !== undefined) onSearch({});
  }, [returned, onSearch]);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const now = new Date();

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PERSONAL_SECURITY_KEY.methods }),
      queryClient.invalidateQueries({ queryKey: PERSONAL_SECURITY_KEY.devices }),
    ]);
  };
  const done = (text: PortalMessage): void => {
    setToast({ text, tone: "done" });
  };
  const problem = (text: PortalMessage): void => {
    setToast({ text, tone: "problem" });
  };

  /** Runs a change; a `step_up_required` answer opens the confirmation and the change is retried once after it. */
  const change = async (run: () => Promise<void>, otherwise: PortalMessage): Promise<void> => {
    setBusy(true);
    setToast(null);
    try {
      await run();
    } catch (raised) {
      if (isStepUpRequired(raised) || isAssuranceRequired(raised)) {
        setPending({
          retry: async () => {
            await run();
          },
          authenticator: isAssuranceRequired(raised),
        });
        return;
      }
      if (raised instanceof ApiError && raised.code === "last_sign_in_method") {
        problem(() => (portalText("core", "personal_security.this_is_your_last_way_to_sign_in_so_it_cannot_be_removed_add_anot_389a28")));
        return;
      }
      if (raised instanceof ApiError && raised.code === "step_up_unavailable") {
        problem(() => (portalText("core", "personal_security.choose_an_organization_first_then_confirm_it_is_you_d9ed80")));
        return;
      }
      problem(() => (apiProblemText(raised, resolvePortalMessage(otherwise))));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (password: string): Promise<void> => {
    const wasSet = methods.data?.password.enabled === true;
    await change(async () => {
      const signedOut = await gateway.changeMyPassword(password);
      setPasswordOpen(false);
      done(() => (passwordChangedText(signedOut, wasSet)));
      await refresh();
    }, () => (portalText("core", "personal_security.the_password_could_not_be_changed_ceb60b")));
  };

  /** plan-028: links Google or Microsoft — a fresh confirmation, then the provider, which brings the person back here. */
  const link = (provider: "google" | "microsoft"): void => {
    void change(async () => {
      const started = await gateway.linkMySignInMethod(provider);
      window.location.assign(started.startUrl);
    }, () => (portalText("core", "personal_security.provider_could_not_be_linked", { value0: METHOD_WORD[provider] })));
  };

  const unlink = (method: RemovableSignInMethod, before: SignInMethodsView): void => {
    void change(async () => {
      await gateway.unlinkMySignInMethod(method);
      done(() => (unlinkedText(method, before)));
      await refresh();
    }, () => (portalText("core", "personal_security._could_not_be_removed_a1dcb0", { value0: METHOD_WORD[method] })));
  };

  const startAuthenticator = (): void => {
    void change(async () => {
      const started = await gateway.startTotpEnrolment();
      setOtpauthUri(started.otpauthUri);
    }, () => (portalText("core", "personal_security.the_authenticator_could_not_be_started_f95e5d")));
  };

  const newCodes = (): void => {
    void change(async () => {
      setCodes(await gateway.regenerateRecoveryCodes());
      done(() => (portalText("core", "personal_security.new_codes_made_the_old_ones_no_longer_work_f0cbce")));
      await refresh();
    }, () => (portalText("core", "personal_security.new_recovery_codes_could_not_be_made_d10e46")));
  };

  const signOutDevice = (device: PersonDeviceView): void => {
    void change(async () => {
      await gateway.revokeMyDevice(device.authSessionId);
      done(() => (portalText("core", "personal_security.that_device_was_signed_out_300689")));
      await refresh();
    }, () => (portalText("core", "personal_security.that_device_could_not_be_signed_out_c60db8")));
  };

  const signOutOthers = (): void => {
    void change(async () => {
      const signedOut = await gateway.signOutMyOtherDevices();
      done(() => signedOutText(signedOut));
      await refresh();
    }, () => (portalText("core", "personal_security.your_other_devices_could_not_be_signed_out_e3b499")));
  };

  /**
   * ADR 0072: the person's own choice; audited by the api into every organization they belong to. ADR 0076 (review
   * finding 1): the wording sent is the one this block shows next to Allow (`help_reading_off`, which says a wetalk
   * person can see the page too) — `HELP_PAGE_READING_REVISION`, the revision of that sentence.
   */
  const setHelpReading = (choice: "allowed" | "declined"): void => {
    setBusy(true);
    setToast(null);
    void gateway
      .setHelpPageReading(choice, HELP_PAGE_READING_REVISION)
      .then(async () => {
        done(() => (choice === "allowed" ? portalText("core", "personal_security.help_reading_allowed") : portalText("core", "personal_security.help_reading_declined")));
        await queryClient.invalidateQueries({ queryKey: PERSONAL_SECURITY_KEY.helpReading });
      })
      .catch((raised: unknown) => {
        problem(() => apiProblemText(raised, portalText("core", "personal_security.help_reading_failed")));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const m = methods.data;

  return (
    <section className="ps-page">
      <h1 className="ps-title">{portalText("core", "personal_security.your_sign_in_and_devices_fd41a7")}</h1>
      <p className="ps-lede">{portalText("core", "personal_security.this_is_your_personal_account_it_is_the_same_in_every_organizatio_2be47b")}</p>
      <div className="ps-tabs" role="tablist" aria-label={portalText("core", "personal_security.my_security_74e348")}>
        <button type="button" role="tab" aria-selected="true" className="ps-tab ps-tab-on">{portalText("core", "personal_security.my_security_74e348")}</button>
      </div>

      {toast === null ? null : (
        <p className={toast.tone === "done" ? "ps-toast" : "ps-toast ps-toast-problem"} role="status">
          <Icon glyph={toast.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="xl" />
          <span>{resolvePortalMessage(toast.text)}</span>
        </p>
      )}

      {/* --- Ways to sign in --------------------------------------------------------------------------------- */}
      <div className="ps-block">
        <BlockHead title={portalText("core", "personal_security.ways_to_sign_in_93a01b")} dek={portalText("core", "personal_security.you_cannot_remove_the_last_one_6ca074")} />
        {m === undefined ? (
          <p className="c-note" role="status">
            {methods.isError ? apiProblemText(methods.error, portalText("core", "personal_security.your_ways_to_sign_in_could_not_be_read_9f1039")) : portalText("core", "personal_security.reading_your_ways_to_sign_in_a0458f")}
          </p>
        ) : (
          <div className="ps-list">
            <Item
              glyph={ITEM_GLYPH.password}
              title={portalText("core", "auth.password_8be3c9")}
              tag={m.password.enabled ? portalText("core", "personal_security.on_e0049a") : null}
              tone={m.password.enabled ? "accent" : "neutral"}
              dek={m.password.enabled && m.password.changedAt !== null ? portalText("core", "personal_security.changed_72f756", { value0: agoText(m.password.changedAt, now) }) : portalText("core", "account_admin.not_set_93039e")}
              action={{ label: m.password.enabled ? portalText("core", "auth.change_64fbd9") : portalText("core", "personal_security.set_448ab7"), onClick: () => setPasswordOpen(true), disabled: busy }}
            />
            {(["google", "microsoft"] as const).map((provider) => {

              const one = m[provider];
              return (
                <Item
                  key={provider}
                  glyph={ITEM_GLYPH[provider]}
                  title={METHOD_WORD[provider]}
                  tag={one.linked ? portalText("core", "personal_security.linked_a089f6") : null}
                  tone={one.linked ? "accent" : "neutral"}
                  dek={one.linked && one.email !== null ? one.email : portalText("core", "personal_security.not_linked_0196fd")}
                  action={
                    one.linked
                      ? { label: portalText("core", "personal_security.unlink_0dc291"), onClick: () => unlink(provider, m), disabled: busy }
                      : one.linkable
                        ? { label: portalText("core", "personal_security.link_provider"), onClick: () => link(provider), disabled: busy }
                        : null
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* --- Second step ----------------------------------------------------------------------------------------- */}
      <div className="ps-block">
        <BlockHead title={portalText("core", "personal_security.second_step_472249")} />
        {m === undefined ? null : (
          <div className="ps-list">
            <Item
              glyph={ITEM_GLYPH.authenticator}
              title={portalText("core", "personal_security.authenticator_app_9e715f")}
              tag={m.totp.state === "active" ? portalText("core", "personal_security.on_e0049a") : null}
              tone={m.totp.state === "active" ? "accent" : "neutral"}
              dek={m.totp.state === "active" ? portalText("core", "personal_security.asked_at_sign_in_and_before_security_changes_62315b") : portalText("core", "personal_security.not_set_up_add_one_to_protect_your_sign_in_feb2e2")}
              {...(m.totp.state === "active" && m.totp.addedAt !== null ? { meta: portalText("core", "personal_security.added_0b64ab", { value0: shortDateText(m.totp.addedAt) }) } : {})}
              action={{ label: m.totp.state === "active" ? portalText("core", "personal_security.replace_a7cf7b") : portalText("core", "personal_security.turn_on_26563e"), onClick: startAuthenticator, disabled: busy }}
            />
            <Item
              glyph={ITEM_GLYPH.recovery}
              title={portalText("core", "start.recovery_codes_b3f5f8")}
              tag={m.totp.state === "active" ? recoveryLeftText(m) : null}
              tone="neutral"
              dek={m.totp.state === "active" ? portalText("core", "personal_security.each_works_once_keep_them_somewhere_safe_9cbbac") : portalText("core", "personal_security.made_when_you_add_an_authenticator_app_4b9816")}
              action={m.totp.state === "active" ? { label: portalText("core", "personal_security.new_codes_b00b4c"), onClick: newCodes, disabled: busy } : null}
            />
            {/* Security review M-6: the authenticator stays required when the codes run out — say so, and offer new ones. */}
            {m.totp.state === "active" && m.recoveryCodeRemaining === 0 ? (
              <p className="c-note c-note-critical" role="status">{portalText("core", "personal_security.you_have_used_all_your_recovery_codes_your_authenticator_app_is_s_78cf43")}</p>
            ) : null}
          </div>
        )}
      </div>

      {/* --- The help assistant's page reading (ADR 0072) ------------------------------------------------------------ */}
      <div className="ps-block">
        <BlockHead title={portalText("core", "personal_security.help_reading_title")} dek={portalText("core", "personal_security.help_reading_dek")} />
        {helpReading.data === undefined ? (
          <p className="c-note" role="status">
            {helpReading.isError ? apiProblemText(helpReading.error, portalText("core", "personal_security.help_reading_unreadable")) : null}
          </p>
        ) : (
          <div className="ps-list">
            <Item
              glyph={ITEM_GLYPH.helpReading}
              title={portalText("core", "personal_security.help_reading_item")}
              tag={helpReading.data.choice === "allowed" ? portalText("core", "personal_security.on_e0049a") : null}
              tone={helpReading.data.choice === "allowed" ? "accent" : "neutral"}
              dek={
                helpReading.data.choice !== "allowed"
                  ? portalText("core", "personal_security.help_reading_off")
                  : helpReading.data.operatorSees
                    ? portalText("core", "personal_security.help_reading_on")
                    : portalText("core", "personal_security.help_reading_on_assistant_only")
              }
              action={
                helpReading.data.choice === "allowed"
                  ? { label: portalText("core", "personal_security.help_reading_stop"), onClick: () => setHelpReading("declined"), disabled: busy }
                  : { label: portalText("core", "personal_security.help_reading_allow"), onClick: () => setHelpReading("allowed"), disabled: busy }
              }
            />
            {/* OD-3 (owner, 2026-10-21): allowed for the assistant only — the newer wording, and Allow, offered again. */}
            {helpReading.data.choice === "allowed" && !helpReading.data.operatorSees ? (
              <Item
                glyph={ITEM_GLYPH.helpReading}
                title={portalText("core", "personal_security.help_reading_person_item")}
                tag={null}
                tone="neutral"
                dek={portalText("core", "personal_security.help_reading_person_dek")}
                action={{ label: portalText("core", "personal_security.help_reading_person_allow"), onClick: () => setHelpReading("allowed"), disabled: busy }}
              />
            ) : null}
          </div>
        )}
      </div>

      {/* --- Where you are signed in ---------------------------------------------------------------------------------- */}
      <div className="ps-block">
        <BlockHead title={portalText("core", "personal_security.where_you_are_signed_in_158b16")}>
          <Button variant="secondary" onClick={signOutOthers} disabled={busy || devices.data === undefined}>{portalText("core", "personal_security.sign_out_everywhere_else_e38e51")}</Button>
        </BlockHead>
        {devices.data === undefined ? (
          <p className="c-note" role="status">
            {devices.isError ? apiProblemText(devices.error, portalText("core", "personal_security.your_devices_could_not_be_read_c9e033")) : portalText("core", "personal_security.reading_where_you_are_signed_in_cc483e")}
          </p>
        ) : (
          <div className="ps-table-wrap">
            <table className="table ps-table">
              <thead>
                <tr>
                  <th>{portalText("core", "personal_security.device_a5a74a")}</th>
                  <th>{portalText("core", "personal_security.where_525f61")}</th>
                  <th>{portalText("core", "workspaces.last_active_b5665e")}</th>
                  <th aria-label={portalText("core", "personal_security.action_97c89a")} />
                </tr>
              </thead>
              <tbody>
                {devices.data.map((device) => (
                  <tr key={device.authSessionId}>
                    <td className="ps-cell-device">
                      <span>{deviceText(device)}</span>
                      {device.current ? <span className="ps-cell-sub">{portalText("core", "personal_security.this_device_eef9f9")}</span> : null}
                    </td>
                    <td className="ps-cell">{placeText(device)}</td>
                    <td className="ps-cell">{device.current ? portalText("core", "personal_security.now_c9bc84") : agoText(device.lastSeenAt, now)}</td>
                    <td className="ps-cell ps-cell-action">
                      {device.current || device.operatorActing ? (
                        "—"
                      ) : (
                        <a
                          href="#"
                          className="ps-link"
                          onClick={(event) => {
                            event.preventDefault();
                            signOutDevice(device);
                          }}
                        >{portalText("core", "start.sign_out_dc1649")}</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {m === undefined ? null : (
        <>
          <ConfirmDialog
            gateway={gateway}
            methods={m}
            pending={pending}
            onDone={() => setPending(null)}
            onProblem={problem}
          />
          <PasswordDialog open={passwordOpen} wasSet={m.password.enabled} onClose={() => setPasswordOpen(false)} onSubmit={changePassword} />
          <AuthenticatorDialog
            gateway={gateway}
            otpauthUri={otpauthUri}
            replacing={m.totp.state === "active"}
            onClose={() => setOtpauthUri(null)}
            onConfirmed={(replaced, recoveryCode) => {
              setOtpauthUri(null);
              done(() => (replaced ? portalText("core", "personal_security.authenticator_app_replaced_74b874") : portalText("core", "personal_security.authenticator_app_turned_on_9d109e")));
              if (recoveryCode !== null) setCodes(recoveryCode);
              void refresh();
            }}
          />
        </>
      )}
      <RecoveryCodesDialog codes={codes} onClose={() => setCodes(null)} />
    </section>
  );
}
